import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaUsers, FaBookOpen, FaChalkboardTeacher, FaHome,
  FaSignOutAlt, FaCreditCard, FaClipboardList,
  FaDollarSign, FaCalendarAlt,
  FaFileContract,
  FaBolt, FaSearch, FaBars, FaChartBar,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

type PaymentStats = {
  inReviewCount: number;
  overdueCount: number;
  paidCount: number;
  totalCount: number;
  totalRevenue: number;
};

// ─── Sidebar nav structure ─────────────────────────────────────────────────
const NAV = [
  {
    id: "finance", label: "Finance", icon: FaDollarSign,
    items: [
      { label: "Manage Payments",      link: "/admin/payment-management",             badge: "payment" },
      { label: "Enrollments & Discounts", link: "/admin/manage-enrollments" },
      { label: "Financial Overview",   link: "/admin/financial-dashboard" },
    ],
  },
  {
    id: "students", label: "Students", icon: FaUsers,
    items: [
      { label: "Manage Students",      link: "/admin/manage-students" },
      { label: "Legacy Enrollment",    link: "/admin/manage-legacy",                  badge: "legacy" },
      { label: "Course Progress",      link: "/admin/course-progress-management" },
    ],
  },
  {
    id: "courses", label: "Courses", icon: FaBookOpen,
    items: [
      { label: "Manage Courses",       link: "/admin/manage-courses" },
      { label: "Manage Batches",       link: "/admin/manage-batches" },
      { label: "Course Regulations",   link: "/admin/regulation-review",              badge: "regulation" },
      { label: "Attendance",           link: "/admin/attendance-overview" },
      { label: "Batch Progress",       link: "/admin/batch-progress-dashboard" },
      { label: "Vocabulary",           link: "/admin/vocabulary-management" },
      { label: "Course Lectures",      link: "/admin/course-lectures" },
    ],
  },
  {
    id: "assessments", label: "Assessments", icon: FaClipboardList,
    items: [
      { label: "Exams",                link: "/admin/exam-management" },
      { label: "Quizzes",              link: "/admin/manage-quizzes" },
      { label: "Homework",             link: "/admin/homework" },
      { label: "Writing Tasks",        link: "/admin/writing-tasks" },
      { label: "Class Tests",          link: "/admin/class-test-management" },
      { label: "Shadowing Tasks",      link: "/admin/shadowing-tasks" },
      { label: "Speaking Practice",    link: "/admin/speaking-practice" },
    ],
  },
  {
    id: "services", label: "Services", icon: FaCalendarAlt,
    items: [
      { label: "Appointments",         link: "/admin/manage-appointments",            badge: "appointment" },
      { label: "Ausbildung Apps",      link: "/admin/ausbildung-application-overview" },
      { label: "Cover Letter Access",  link: "/admin/cover-letter-access" },
      { label: "CV Builder Access",    link: "/admin/cv-builder-access" },
      { label: "Calendar",             link: "/admin/calendar" },
    ],
  },
  {
    id: "staff", label: "Staff & Content", icon: FaChalkboardTeacher,
    items: [
      { label: "Manage Mentors",       link: "/admin/manage-mentors" },
      { label: "Manage Blog",          link: "/admin/manage-blogs" },
      { label: "Monthly Visits",       link: "/admin/monthly-visits" },
    ],
  },
  {
    id: "reports", label: "Reports", icon: FaChartBar,
    items: [
      { label: "Activity Report",      link: "/admin/activity-report" },
      { label: "Mentor Tracker",       link: "/admin/mentor-tracker" },
    ],
  },
];

// ─── Sidebar ──────────────────────────────────────────────────────────────
const Sidebar: React.FC<{
  open: boolean;
  onClose: () => void;
  badges: Record<string, number>;
  onLogout: () => void;
  student: any;
}> = ({ open, onClose, badges, onLogout, student }) => {
  const location = useLocation();
  const [expanded, setExpanded] = useState<string[]>(["finance"]);

  const toggle = (id: string) =>
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const getBadge = (key?: string) => (key ? badges[key] ?? 0 : 0);

  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed md:static top-0 left-0 h-[100dvh] md:h-full w-64 bg-gradient-to-b from-[#009fe3] to-[#0077b6] text-white flex flex-col z-50 transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/20 flex-shrink-0 relative">
          <div className="text-xl font-extrabold tracking-tight">
            <span className="text-white">Guideline</span>
            <span className="text-red-400">2</span>
            <span className="text-yellow-300">Germany</span>
          </div>
          <div className="text-xs text-white/70 mt-0.5">Admin Panel</div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-0.5 text-sm">
          {/* Dashboard link */}
          <Link
            to="/admin/dashboard"
            onClick={onClose}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-semibold transition-all border-l-[3px] ${
              location.pathname === "/admin/dashboard"
                ? "bg-white/15 border-white"
                : "border-transparent hover:bg-white/10"
            }`}
          >
            <FaHome className="w-4 h-4 flex-shrink-0" />
            Dashboard
          </Link>

          {/* Categories */}
          {NAV.map((cat) => {
            const Icon = cat.icon;
            const isOpen = expanded.includes(cat.id);
            const totalBadge = cat.items.reduce((sum, item) => sum + getBadge(item.badge), 0);

            return (
              <div key={cat.id}>
                <button
                  onClick={() => toggle(cat.id)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg font-semibold transition-all border-l-[3px] border-transparent hover:bg-white/10"
                >
                  <span className="flex items-center gap-3">
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {cat.label}
                  </span>
                  <span className="flex items-center gap-2">
                    {totalBadge > 0 && (
                      <span className="bg-amber-400 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                        {totalBadge}
                      </span>
                    )}
                    <svg
                      className={`w-3.5 h-3.5 opacity-70 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                      fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>

                {isOpen && (
                  <div className="ml-4 border-l border-white/20 pl-3 space-y-0.5 mb-1">
                    {cat.items.map((item) => {
                      const count = getBadge(item.badge);
                      const active = location.pathname === item.link;
                      return (
                        <Link
                          key={item.link}
                          to={item.link}
                          onClick={onClose}
                          className={`flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-white/90 transition-all border-l-[3px] ${
                            active
                              ? "bg-white/15 border-white text-white font-semibold"
                              : "border-transparent hover:bg-white/10"
                          }`}
                        >
                          <span className="truncate">{item.label}</span>
                          {count > 0 && (
                            <span className="flex-shrink-0 bg-amber-400 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                              {count}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="px-4 py-4 border-t border-white/20 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white text-[#009fe3] flex items-center justify-center text-sm font-bold flex-shrink-0">
              {student?.firstName?.[0]?.toUpperCase() ?? "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-white">
                {student?.firstName} {student?.lastName}
              </p>
              <p className="text-xs text-white/60 capitalize truncate">{student?.role?.toLowerCase()}</p>
            </div>
            <button
              onClick={onLogout}
              title="Logout"
              className="text-white/70 hover:text-white transition p-1"
            >
              <FaSignOutAlt />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// ─── Main Dashboard ────────────────────────────────────────────────────────
const AdminDashboard: React.FC = () => {
  const { logout, student } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = React.useRef<HTMLElement>(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentStats, setPaymentStats] = useState<PaymentStats | null>(null);
  const [legacyPendingCount, setLegacyPendingCount] = useState(0);
  const [regulationPendingCount, setRegulationPendingCount] = useState(0);
  const [appointmentStats, setAppointmentStats] = useState({ pendingPayment: 0, pending: 0, invited: 0 });

  const API = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    // Payments
    fetch(`${API}/api/admin/payments/stats`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setPaymentStats(d))
      .catch(() => {});

    // Legacy
    fetch(`${API}/api/admin/legacy/pending`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setLegacyPendingCount(Array.isArray(d) ? d.length : Number(d?.count ?? 0)))
      .catch(() => {});

    // Regulations
    fetch(`${API}/api/admin/regulations/pending/count`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : 0)
      .then((n) => setRegulationPendingCount(Number(n) || 0))
      .catch(() => {});

    // Appointments
    fetch(`${API}/api/admin/appointments`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : [])
      .then((list) => {
        const arr = Array.isArray(list) ? list : Array.isArray(list?.data) ? list.data : [];
        let pendingPayment = 0, pending = 0, invited = 0;
        for (const a of arr) {
          const s = (a?.status ?? "").toString().trim().toUpperCase();
          if (s === "PENDING_PAYMENT") pendingPayment++;
          else if (s === "PENDING") pending++;
          else if (s === "INVITED" || s === "SENT") invited++;
        }
        setAppointmentStats({ pendingPayment, pending, invited });
      })
      .catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate("/"); };

  const badges: Record<string, number> = {
    payment:    paymentStats?.inReviewCount ?? 0,
    legacy:     legacyPendingCount,
    regulation: regulationPendingCount,
    appointment: appointmentStats.pendingPayment + appointmentStats.pending,
  };

  const quickActions = [
    {
      title: "Manage Payments",
      desc: "Review and approve payment requests",
      icon: FaCreditCard,
      link: "/admin/payment-management",
      gradient: "from-green-50 to-emerald-50",
      border: "border-green-100",
      iconColor: "text-green-600",
      badgeKey: "payment",
      badgeColor: "bg-amber-400 text-black",
    },
    {
      title: "Course Regulations",
      desc: "Upload templates and review student regulations",
      icon: FaFileContract,
      link: "/admin/regulation-review",
      gradient: "from-purple-50 to-fuchsia-50",
      border: "border-purple-100",
      iconColor: "text-purple-600",
      badgeKey: "regulation",
      badgeColor: "bg-purple-500 text-white",
    },
    {
      title: "Appointments",
      desc: "Schedule and manage student appointments",
      icon: FaCalendarAlt,
      link: "/admin/manage-appointments",
      gradient: "from-orange-50 to-amber-50",
      border: "border-orange-100",
      iconColor: "text-orange-500",
      badgeKey: "appointment",
      badgeColor: "bg-orange-400 text-white",
    },
  ];

  // Flat list for search
  const allItems = NAV.flatMap((cat) =>
    cat.items.map((item) => ({ ...item, category: cat.label }))
  );
  const searchResults = searchQuery.length > 1
    ? allItems.filter((i) =>
        i.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        i.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const totalPending = Object.values(badges).reduce((a, b) => a + b, 0);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        badges={badges}
        onLogout={handleLogout}
        student={student}
      />

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 relative z-0">

        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition flex-shrink-0"
          >
            <FaBars />
          </button>

          <h1 className="text-base sm:text-lg font-bold text-gray-800 flex-shrink-0">Dashboard</h1>

          {/* Search */}
          <div className="relative flex-1 max-w-sm ml-2">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]/30 focus:border-[#009fe3]"
            />
            {/* Search dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                {searchResults.slice(0, 8).map((item) => (
                  <Link
                    key={item.link}
                    to={item.link}
                    onClick={() => setSearchQuery("")}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition"
                  >
                    <span className="text-sm font-medium text-gray-800">{item.label}</span>
                    <span className="text-xs text-gray-400">{item.category}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {totalPending > 0 && (
              <div className="relative">
                <span className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition cursor-pointer">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </span>
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {totalPending > 99 ? "99+" : totalPending}
                </span>
              </div>
            )}
            <Link to="/" className="hidden sm:block text-sm text-gray-500 hover:text-gray-700 transition">
              ← View Site
            </Link>
          </div>
        </header>

        {/* Content */}
        <main ref={mainRef} className="flex-1 overflow-y-auto p-4 sm:p-6">

          {/* KPI Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {[
              { label: "Pending Payments",    value: paymentStats?.inReviewCount ?? 0,  icon: FaCreditCard,    bg: "bg-amber-100",  iconColor: "text-amber-600",  link: "/admin/payment-management" },
              { label: "Pending Regulations", value: regulationPendingCount,             icon: FaFileContract,  bg: "bg-purple-100", iconColor: "text-purple-600", link: "/admin/regulation-review" },
              { label: "Appointments",        value: appointmentStats.pending + appointmentStats.pendingPayment, icon: FaCalendarAlt, bg: "bg-blue-100", iconColor: "text-blue-600", link: "/admin/manage-appointments" },
            ].map((stat) => {
              const Icon = stat.icon;
              return (
                <Link
                  key={stat.label}
                  to={stat.link}
                  className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 flex items-center gap-3 sm:gap-4 hover:shadow-md transition"
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 ${stat.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`${stat.iconColor} text-lg sm:text-xl`} />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 leading-tight">{stat.label}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <FaBolt className="text-[#009fe3]" />
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {quickActions.map((qa) => {
                const Icon = qa.icon;
                const count = badges[qa.badgeKey] ?? 0;
                return (
                  <Link
                    key={qa.link}
                    to={qa.link}
                    className={`bg-gradient-to-br ${qa.gradient} border ${qa.border} rounded-xl p-4 hover:shadow-md transition group`}
                  >
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm mb-3">
                      <Icon className={`${qa.iconColor} text-lg`} />
                    </div>
                    <p className="font-semibold text-gray-800 text-sm leading-tight mb-1">{qa.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed hidden sm:block mb-2">{qa.desc}</p>
                    {count > 0 && (
                      <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${qa.badgeColor}`}>
                        {count} pending
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Stats summary row */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6">
            <h3 className="font-bold text-gray-800 mb-4">Payment Overview</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "In Review",     value: paymentStats?.inReviewCount ?? 0,  color: "text-amber-600" },
                { label: "Overdue",       value: paymentStats?.overdueCount ?? 0,   color: "text-red-500" },
                { label: "Paid",          value: paymentStats?.paidCount ?? 0,      color: "text-green-600" },
                { label: "Total Revenue", value: paymentStats ? `৳${(paymentStats.totalRevenue / 1000).toFixed(0)}K` : "—", color: "text-[#009fe3]" },
              ].map((s) => (
                <div key={s.label} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className={`text-xl sm:text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
